import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./RateByBarcode.scss";
import konfidencyLogo from "./konfidency-logo.png";
import { RateProduct } from "./RateProduct";

// ~1MB de WASM: só entra no bundle de quem realmente abre a câmera.
const BarcodeScanner = lazy(() => import("./BarcodeScanner"));

const ACCENT_PADRAO = "#211a44";

// `settings.primaryColor` vem da configuração do lojista sem nenhuma validação, e a
// derivação por sufixo de alpha ("+14") só funciona sobre hex de 6 dígitos. Formato
// inesperado cai inteiro no default em vez de gerar cor quebrada.
const normalizarAccent = (bruto) => {
  if (typeof bruto !== "string") return null;

  const valor = bruto.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(valor)) return "#" + valor.slice(1).split("").map((c) => c + c).join("");
  if (/^#[0-9a-f]{6}$/.test(valor)) return valor;
  if (/^#[0-9a-f]{8}$/.test(valor)) return valor.slice(0, 7);

  return null;
};

const construirTema = (primaryColor) => {
  const accent = normalizarAccent(primaryColor) || ACCENT_PADRAO;

  return {
    "--store-accent": accent,
    "--store-accent-soft": accent + "14",
    "--store-accent-halo": accent + "2e",
    // A linha de varredura precisa contrastar com a câmera escura; quando o lojista
    // não tem cor própria o accent é o próprio ink e sumiria no fundo.
    "--store-accent-bright": accent === ACCENT_PADRAO ? "#ff4fbb" : accent,
  };
};

// Dígito verificador GTIN. Usado só como AVISO: o catálogo real tem códigos que não
// fecham a conta, e bloquear o envio tornaria esses produtos inalcançáveis.
const digitoVerificadorValido = (codigo) => {
  const digitos = codigo.replace(/\D/g, "");
  if (digitos.length < 8) return false;

  const corpo = digitos.slice(0, -1).split("").reverse().map(Number);
  const soma = corpo.reduce((acc, n, i) => acc + n * (i % 2 === 0 ? 3 : 1), 0);

  return (10 - (soma % 10)) % 10 === Number(digitos.slice(-1));
};

const ehIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

export function RateByBarcode() {
  const { customer } = useParams();
  const { t, i18n } = useTranslation();

  const [screen, setScreen] = useState("loading");
  const [customerInfo, setCustomerInfo] = useState(null);
  const [produto, setProduto] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [codigoLido, setCodigoLido] = useState("");
  const [origem, setOrigem] = useState("barcode-manual");
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState(null);
  const [avaliacaoEnviada, setAvaliacaoEnviada] = useState(false);

  // Espelha a tela atual para os callbacks assíncronos do scanner, que capturariam
  // um closure obsoleto de `screen`.
  const screenRef = useRef(screen);
  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  useEffect(() => {
    const carregar = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/${customer}/info`);
        setCustomerInfo(data);

        // Gate na ENTRADA do fluxo. Sem esta flag e sem pedido vinculado, o botão de
        // enviar do RateProduct fica permanentemente desabilitado e sem nenhuma
        // pista visual — o consumidor preencheria tudo para descobrir no fim.
        setScreen(data?.enableUnauthenticatedReviews ? "intro" : "unavailable");
      } catch (err) {
        setScreen("error");
      }
    };

    carregar();
  }, [customer]);

  useEffect(() => {
    if (customerInfo?.settings?.locale) i18n.changeLanguage(customerInfo.settings.locale);
  }, [customerInfo, i18n]);

  const buscarProduto = useCallback(
    async (valor, origemLeitura) => {
      setBuscando(true);
      setErroBusca(null);
      setCodigoLido(valor);
      setOrigem(origemLeitura);

      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/${customer}/barcode`,
          { params: { code: valor } }
        );

        const encontrado = data?.products?.[0];

        if (!encontrado) {
          setScreen("notFound");
          return;
        }

        if (encontrado.reviewBlocked) {
          setScreen("blocked");
          return;
        }

        setProduto({ product: encontrado, sku: encontrado.sku, customer });
        setAvaliacaoEnviada(false);
        setScreen("reviewForm");
      } catch (err) {
        setErroBusca(err?.response?.status === 429 ? "rate_limited" : "generic");
        setScreen("error");
      } finally {
        setBuscando(false);
      }
    },
    [customer]
  );

  const aoDetectar = useCallback((valor) => buscarProduto(valor, "barcode-scan"), [buscarProduto]);
  const aoNegarPermissao = useCallback(() => setScreen("permissionDenied"), []);
  const aoFalharCamera = useCallback(() => setScreen("permissionDenied"), []);

  const voltarParaLeitura = () => {
    setProduto(null);
    setCodigo("");
    setCodigoLido("");
    setAvaliacaoEnviada(false);
    setScreen("scanning");
  };

  const tema = construirTema(customerInfo?.settings?.primaryColor);
  const digitos = codigo.replace(/\D/g, "");
  const comprimentoValido = digitos.length >= 8 && digitos.length <= 14;
  const avisoDigito = comprimentoValido && !digitoVerificadorValido(digitos);

  const cabecalho = (comBotaoVoltar, aoVoltar) => (
    <header className="rbb-header">
      {comBotaoVoltar ? (
        <button type="button" className="rbb-back" onClick={aoVoltar} aria-label={t("alt.back")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      ) : (
        <span className="rbb-spacer" />
      )}

      {customerInfo?.logo ? (
        <img className="rbb-logo" src={customerInfo.logo} alt={t("alt.customer_logo", { name: customerInfo.name })} />
      ) : (
        <span className="rbb-store-name">{customerInfo?.name}</span>
      )}

      <span className="rbb-spacer" />
    </header>
  );

  const conteudo = () => {
    if (screen === "loading") {
      return <div className="rbb-center rbb-muted">{t("barcode.loading")}</div>;
    }

    if (screen === "unavailable") {
      return (
        <>
          {cabecalho(false)}
          <div className="rbb-body">
            <h2>{t("barcode.unavailable.title")}</h2>
            <p className="rbb-lead">{t("barcode.unavailable.body")}</p>
          </div>
        </>
      );
    }

    if (screen === "intro") {
      return (
        <>
          {cabecalho(false)}
          <div className="rbb-body">
            <span className="rbb-eyebrow">{t("barcode.intro.eyebrow")}</span>
            <h2>{t("barcode.intro.title")}</h2>
            <p className="rbb-lead">{t("barcode.intro.body")}</p>

            <ol className="rbb-steps">
              {["scan", "confirm", "review"].map((passo, indice) => (
                <li key={passo}>
                  <span className="rbb-step-marker">{indice + 1}</span>
                  <span>
                    <strong>{t(`barcode.intro.steps.${passo}_title`)}</strong>
                    <span className="rbb-step-body">{t(`barcode.intro.steps.${passo}_body`)}</span>
                  </span>
                </li>
              ))}
            </ol>

            <p className="rbb-note">{t("barcode.intro.permission_note")}</p>

            <div className="rbb-footer">
              {/* A permissão de câmera é pedida SÓ neste clique, nunca no load: é a
                  razão de existir esta tela — dar contexto antes do prompt do navegador. */}
              <button type="button" className="rbb-cta" onClick={() => setScreen("scanning")}>
                {t("barcode.intro.cta")}
              </button>
              <button type="button" className="rbb-ghost" onClick={() => setScreen("manualEntry")}>
                {t("barcode.intro.secondary")}
              </button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "scanning") {
      return (
        <>
          {cabecalho(true, () => setScreen("intro"))}
          <div className="rbb-body">
            <h3>{t("barcode.scanning.title")}</h3>
            <p className="rbb-lead">{t("barcode.scanning.body")}</p>

            <Suspense fallback={<div className="rbb-camera-placeholder">{t("barcode.scanning.starting")}</div>}>
              <BarcodeScanner
                onDetected={aoDetectar}
                onPermissionDenied={aoNegarPermissao}
                onError={aoFalharCamera}
              />
            </Suspense>

            <p className="rbb-hint">{t("barcode.scanning.hint")}</p>

            <div className="rbb-footer">
              <button type="button" className="rbb-outline" onClick={() => setScreen("manualEntry")}>
                {t("barcode.scanning.manual_link")}
              </button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "manualEntry") {
      return (
        <>
          {cabecalho(true, () => setScreen("intro"))}
          <div className="rbb-body">
            <h3>{t("barcode.manual.title")}</h3>
            <p className="rbb-lead">{t("barcode.manual.body")}</p>

            <form
              className="rbb-manual-form"
              onSubmit={(evt) => {
                evt.preventDefault();
                if (comprimentoValido && !buscando) buscarProduto(digitos, "barcode-manual");
              }}
            >
              <label className="rbb-label" htmlFor="barcode-input">
                {t("barcode.manual.label")}
              </label>
              <input
                id="barcode-input"
                className="rbb-input"
                // inputMode numérico abre o teclado do sistema; não desenhamos teclado.
                inputMode="numeric"
                autoComplete="off"
                maxLength={14}
                value={codigo}
                onChange={(evt) => setCodigo(evt.target.value.replace(/\D/g, ""))}
              />

              <div className="rbb-input-meta">
                <span>{t("barcode.manual.helper")}</span>
                <span className="rbb-counter">{digitos.length}</span>
              </div>

              {codigo.length > 0 && !comprimentoValido && (
                <p className="rbb-warning">{t("barcode.manual.invalid_length")}</p>
              )}
              {/* Aviso, não bloqueio: há códigos legítimos no catálogo que não fecham
                  o dígito verificador, e travar aqui os tornaria inalcançáveis. */}
              {avisoDigito && <p className="rbb-warning">{t("barcode.manual.invalid_checkdigit")}</p>}

              <button type="submit" className="rbb-cta" disabled={!comprimentoValido || buscando}>
                {buscando ? t("barcode.manual.searching") : t("barcode.manual.submit")}
              </button>
            </form>

            <div className="rbb-footer">
              <button type="button" className="rbb-ghost" onClick={() => setScreen("scanning")}>
                {t("barcode.manual.scan_link")}
              </button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "notFound") {
      return (
        <>
          {cabecalho(true, () => setScreen("intro"))}
          <div className="rbb-body">
            <span className="rbb-icon warning" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3M14 8l-6 6M8 8l6 6" />
              </svg>
            </span>

            <h2>{t("barcode.not_found.title")}</h2>
            <p className="rbb-lead">{t("barcode.not_found.body")}</p>

            <div className="rbb-code-card">
              <span className="rbb-code-label">{t("barcode.not_found.code_label")}</span>
              <span className="rbb-code-value">{codigoLido}</span>
            </div>

            <p className="rbb-tips-title">{t("barcode.not_found.tips_title")}</p>
            <ul className="rbb-tips">
              <li>{t("barcode.not_found.tip_digits")}</li>
              <li>{t("barcode.not_found.tip_multiple")}</li>
              <li>{t("barcode.not_found.tip_other_brand")}</li>
            </ul>

            <div className="rbb-footer">
              <button type="button" className="rbb-cta" onClick={() => setScreen("scanning")}>
                {t("barcode.not_found.retry")}
              </button>
              <button
                type="button"
                className="rbb-ghost"
                onClick={() => {
                  setCodigo("");
                  setScreen("manualEntry");
                }}
              >
                {t("barcode.not_found.manual_link")}
              </button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "blocked") {
      return (
        <>
          {cabecalho(true, () => setScreen("intro"))}
          <div className="rbb-body">
            <h2>{t("barcode.blocked.title")}</h2>
            <p className="rbb-lead">{t("barcode.blocked.body")}</p>

            <div className="rbb-footer">
              <button type="button" className="rbb-cta" onClick={() => setScreen("scanning")}>
                {t("barcode.not_found.retry")}
              </button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "permissionDenied") {
      // O texto exato do caminho muda por navegador; iOS é o caso que mais confunde
      // porque a permissão fica presa em Ajustes, fora da página.
      const passos = ehIOS()
        ? ["step_ios_1", "step_ios_2", "step_ios_3"]
        : ["step_generic_1", "step_generic_2", "step_generic_3"];

      return (
        <>
          {cabecalho(false)}
          <div className="rbb-body">
            <span className="rbb-icon danger" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 2l20 20M7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h13M14.5 5.5 16 4h2l1.5 1.5H20a2 2 0 0 1 2 2V16" />
              </svg>
            </span>

            <h2>{t("barcode.permission_denied.title")}</h2>
            <p className="rbb-lead">{t("barcode.permission_denied.body")}</p>

            <div className="rbb-howto">
              <p className="rbb-howto-title">{t("barcode.permission_denied.howto_title")}</p>
              <ol>
                {passos.map((passo, indice) => (
                  <li key={passo}>
                    <span className="rbb-howto-marker">{indice + 1}</span>
                    <span>{t(`barcode.permission_denied.${passo}`)}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rbb-footer">
              {/* A ação primária é o caminho que funciona AGORA. Insistir na câmera
                  não adianta: depois de negada, o getUserMedia costuma falhar sem
                  reabrir o prompt. */}
              <button type="button" className="rbb-cta" onClick={() => setScreen("manualEntry")}>
                {t("barcode.permission_denied.manual_cta")}
              </button>
              <button type="button" className="rbb-ghost" onClick={() => setScreen("scanning")}>
                {t("barcode.permission_denied.retry")}
              </button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "error") {
      return (
        <>
          {cabecalho(false)}
          <div className="rbb-body">
            <h2>{t("barcode.error.title")}</h2>
            <p className="rbb-lead">
              {erroBusca === "rate_limited" ? t("barcode.error.rate_limited") : t("barcode.error.body")}
            </p>

            <div className="rbb-footer">
              <button type="button" className="rbb-cta" onClick={() => setScreen(customerInfo ? "intro" : "loading")}>
                {t("barcode.error.retry")}
              </button>
            </div>
          </div>
        </>
      );
    }

    if (screen === "reviewForm" && produto) {
      return (
        <>
          {/* O CSS do lojista entra só aqui. No scanner ele não teria o que estilizar
              e teria muito o que quebrar numa UI de câmera. */}
          {customerInfo?.importCssWebApp && <link rel="stylesheet" href={customerInfo.importCssWebApp} />}
          {customerInfo?.settings?.collectionInterface?.customCss && (
            <style>{customerInfo.settings.collectionInterface.customCss}</style>
          )}

          {cabecalho(true, voltarParaLeitura)}

          <div className="rbb-success-banner">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            {t("barcode.found.banner")}
          </div>

          <div className="rbb-product">
            {produto.product?.image ? (
              <img src={produto.product.image} alt={t("alt.product_image", { name: produto.product?.name })} />
            ) : (
              <span className="rbb-product-thumb-empty" aria-hidden="true" />
            )}

            <div className="rbb-product-info">
              <span className="rbb-product-name">{produto.product?.name}</span>
              <span className="rbb-product-code">{t("barcode.found.code", { code: codigoLido })}</span>
              {!avaliacaoEnviada && (
                <button type="button" className="rbb-wrong-product" onClick={voltarParaLeitura}>
                  {t("barcode.found.wrong_product")}
                </button>
              )}
            </div>
          </div>

          <RateProduct
            product={produto}
            customerInfo={customerInfo}
            source={origem}
            hideProductDetails
            onComplete={() => setAvaliacaoEnviada(true)}
            getNextItem={() => ({ type: "product" })}
            onNavigateNext={voltarParaLeitura}
          />

          <div className="rbb-attribution">
            {t("barcode.attribution")}
            <img src={konfidencyLogo} alt="Konfidency" />
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="rate-by-barcode" style={tema}>
      {conteudo()}
    </div>
  );
}
